import * as yup from 'yup'
import type { DataAccessFormValues, WhoMode, ScopeMode } from '../../types'
import type { CreateDataAccessAssignmentPayload } from '../../types'

/*
 * FE → BE PAYLOAD MAPPING (formToPayload)
 * ────────────────────────────────────────────────────────────────────────────
 * FE state                                          BE payload field
 * ─────────────────────────────────────────         ────────────────────────
 * scope_mode='company' OR who_mode='company'     → apply: 'companies'
 * otherwise                                      → apply: 'teams'
 * assignees                                      → assignees: assignees
 * who_departments ∪ scope_departments (deduped)  → departments: [...]
 * teammates (scope)                              → teammates: [...]
 * ignores                                        → ignores: [...]
 * reject_columns                                 → reject_columns: [...]
 * keycloak_name                                  → keycloak_name
 * name, description                              → name, description
 *
 * NOTE on departments dedup:
 *   Both who_departments and scope_departments map to the same BE departments[]
 *   field. We union them. On edit prefill we can't cleanly split them back — we
 *   put the full BE departments[] into who_departments and leave scope_departments
 *   empty, unless scope_mode is inferred as 'departments'. See prefillFromBE().
 * ────────────────────────────────────────────────────────────────────────────
 *
 * REVERSE MAPPING (prefillFromBE) — best-effort heuristic:
 *   who_mode:
 *     assignees.length > 0                        → 'employees'
 *     apply='companies' && departments.length=0   → 'company'
 *     departments.length > 0                      → 'departments'
 *     fallback                                    → 'employees'
 *   scope_mode:
 *     apply='companies'                           → 'company'
 *     teammates.length > 0                        → 'self_plus_teammates'
 *     else                                        → 'departments'
 *   who_departments / scope_departments:
 *     If both who_mode='departments' AND scope_mode='departments', the same
 *     departments[] feeds both — we put all into who_departments and none in
 *     scope_departments. User can re-pick if needed.
 * ────────────────────────────────────────────────────────────────────────────
 */

export const dataAccessFormSchema = yup.object({
    name: yup
        .string()
        .required('Tên rule là bắt buộc')
        .min(2, 'Tên rule phải có ít nhất 2 ký tự')
        .max(255, 'Tên rule không vượt quá 255 ký tự'),

    description: yup.string().max(500, 'Mô tả không vượt quá 500 ký tự'),

    keycloak_name: yup.string().required('Vui lòng chọn Resource'),

    who_mode: yup
        .mixed<WhoMode>()
        .oneOf(['employees', 'departments', 'company'])
        .required('Vui lòng chọn đối tượng áp dụng'),

    assignees: yup
        .array(yup.number().required())
        .default([])
        .when('who_mode', {
            is: 'employees',
            then: (s) => s.min(1, 'Vui lòng chọn ít nhất một nhân viên'),
        }),

    who_departments: yup
        .array(yup.number().required())
        .default([])
        .when('who_mode', {
            is: 'departments',
            then: (s) => s.min(1, 'Vui lòng chọn ít nhất một phòng ban'),
        }),

    ignores: yup.array(yup.number().required()).default([]),

    scope_mode: yup
        .mixed<ScopeMode>()
        .oneOf(['self_plus_teammates', 'departments', 'company'])
        .required('Vui lòng chọn phạm vi dữ liệu'),

    teammates: yup.array(yup.number().required()).default([]),

    scope_departments: yup
        .array(yup.number().required())
        .default([])
        .when('scope_mode', {
            is: 'departments',
            then: (s) => s.min(1, 'Vui lòng chọn ít nhất một phòng ban'),
        }),

    reject_columns: yup.array(yup.string().required()).default([]),
})

// ─── Transform helpers ───────────────────────────────────────────────────────

export function formToPayload(
    values: DataAccessFormValues
): CreateDataAccessAssignmentPayload {
    const usesCompany =
        values.scope_mode === 'company' || values.who_mode === 'company'

    const depts = Array.from(
        new Set([...values.who_departments, ...values.scope_departments])
    )

    return {
        name: values.name,
        description: values.description || '',
        apply: usesCompany ? 'companies' : 'teams',
        keycloak_name: values.keycloak_name,
        assignees: values.assignees,
        departments: depts,
        teammates: values.teammates,
        ignores: values.ignores,
        reject_columns: values.reject_columns,
    }
}

import type { DataAccessAssignment } from '../../types'

export function prefillFromBE(a: DataAccessAssignment): DataAccessFormValues {
    const whoMode: WhoMode =
        (a.assignees || []).length > 0
            ? 'employees'
            : a.apply === 'companies' && (a.departments || []).length === 0
              ? 'company'
              : (a.departments || []).length > 0
                ? 'departments'
                : 'employees'

    const scopeMode: ScopeMode =
        a.apply === 'companies'
            ? 'company'
            : (a.teammates || []).length > 0
              ? 'self_plus_teammates'
              : 'departments'

    return {
        name: a.name,
        description: a.description || '',
        keycloak_name: a.keycloak_name || '',
        who_mode: whoMode,
        assignees: a.assignees || [],
        who_departments: a.departments || [],
        ignores: a.ignores || [],
        scope_mode: scopeMode,
        teammates: a.teammates || [],
        scope_departments: [],
        reject_columns: a.reject_columns || [],
    }
}
